import { useState, useRef, useEffect } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Globe, Check } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { createPortal } from 'react-dom'
import { LOCALES, DEFAULT_LANG, isLang } from '../../config/locales'
import { menuLanguages } from '../../lib/languages'
import { swapLangInPath } from '../../lib/swapLangInPath'

interface LanguageSwitcherProps {
  onOpen?: () => void
}

export default function LanguageSwitcher({ onOpen }: LanguageSwitcherProps = {}) {
  const { lang = DEFAULT_LANG } = useParams<{ lang: string }>()
  const location = useLocation()
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [panelStyle, setPanelStyle] = useState<{ top: number; right: number }>({ top: 0, right: 0 })
  const btnRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  // Recalculate panel position whenever it opens. Anchor the panel's right
  // edge to the button, but clamp so it never overflows the left viewport
  // edge on narrow screens (the button sits near the right edge, so a wide
  // panel anchored there would otherwise bleed off the left - see the
  // cut-off flags bug). Keep an 8px gutter on both sides.
  useEffect(() => {
    if (!open || !btnRef.current) return
    const GUTTER = 8
    const PANEL_W = 300 // matches minWidth below
    const r = btnRef.current.getBoundingClientRect()
    const vw = window.innerWidth
    let right = vw - r.right
    // If anchoring to the button's right pushes the panel's left edge past the
    // gutter, pull `right` in so left lands exactly at the gutter.
    const maxRight = vw - PANEL_W - GUTTER
    if (right > maxRight) right = Math.max(GUTTER, maxRight)
    setPanelStyle({ top: r.bottom + 10, right })
  }, [open])

  useEffect(() => {
    if (!open) return
    function onPointer(e: PointerEvent) {
      const target = e.target as Node
      if (
        btnRef.current && !btnRef.current.contains(target) &&
        panelRef.current && !panelRef.current.contains(target)
      ) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const displayCode = isLang(lang)
    ? lang.charAt(0).toUpperCase() + lang.slice(1).toLowerCase()
    : lang.toUpperCase()

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => {
          const next = !open
          if (next) onOpen?.()
          setOpen(next)
        }}
        aria-label={t('nav.languageMenu')}
        aria-expanded={open}
        aria-haspopup="true"
        className={`flex items-center gap-1.5 transition-colors px-2 py-2 -m-2 min-h-[44px] text-sm font-body ${
          open ? 'text-gold' : 'text-ivory/65 hover:text-gold'
        }`}
      >
        {isLang(lang) ? (
          <img
            src={LOCALES[lang].flag}
            alt=""
            aria-hidden="true"
            width={20}
            height={14}
            className="h-3.5 w-5 shrink-0 rounded-[2px] object-cover"
          />
        ) : (
          <Globe size={16} aria-hidden="true" />
        )}
        {displayCode}
      </button>

      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              ref={panelRef}
              initial={{ opacity: 0, y: -6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.98 }}
              transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
              className="fixed z-[200] bg-ivory rounded-2xl ring-1 ring-gold/40 ring-offset-[6px] ring-offset-ivory-dark shadow-[var(--shadow-gold-lift)]"
              style={{
                top: panelStyle.top,
                right: panelStyle.right,
                width: 'min(300px, calc(100vw - 16px))',
              }}
            >
              {/* Header */}
              <div className="flex items-center gap-2 px-4 pt-4 pb-3 border-b border-mocha/10">
                <Globe size={13} className="text-mocha/40 shrink-0" aria-hidden="true" />
                <span className="font-body text-[11px] tracking-[0.1em] text-mocha/45 uppercase">
                  {t('nav.selectLanguage', 'Select Language')}
                </span>
              </div>

              {/* 2-col grid */}
              <div className="grid grid-cols-2 gap-x-1 gap-y-0 p-3">
                {/* menuLanguages: every locale minus any an admin has hidden
                    (Russian, Sections tab) - the current one always stays. */}
                {menuLanguages(lang).map((l) => {
                  const isActive = lang === l
                  const href = swapLangInPath(location.pathname, location.search, location.hash, l)
                  return (
                    <Link
                      key={l}
                      to={href}
                      onClick={() => setOpen(false)}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl transition-colors duration-150 min-h-[40px] ${
                        isActive
                          ? 'text-mocha font-medium'
                          : 'text-mocha/60 hover:text-mocha hover:bg-mocha/5'
                      }`}
                    >
                      <img
                        src={LOCALES[l].flag}
                        alt=""
                        aria-hidden="true"
                        width={20}
                        height={14}
                        loading="lazy"
                        className="h-3.5 w-5 shrink-0 rounded-[2px] object-cover"
                      />
                      <span className="font-body text-sm">{LOCALES[l].label}</span>
                      {isActive && (
                        <Check size={12} className="ml-auto text-gold shrink-0" aria-hidden="true" />
                      )}
                    </Link>
                  )
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  )
}
